import { useAppDispatch } from "hooks/store";
import { useFontLoader } from "hooks/useFontLoader";
import { useThemingPrefix } from "hooks/useThemingPrefix";
import React, { useEffect, useMemo } from "react";
import { Outlet } from "react-router";
import { setThemeElements } from "store/slices/themeSlice";
import { themeRegistry } from "themes/registry";

export const ThemeWrapper = () => {
  const currentVendor = useThemingPrefix();
  const theme = useMemo(
    () => themeRegistry[currentVendor] || themeRegistry.default,
    [currentVendor]
  );
  const dispatch = useAppDispatch();

  // Load fonts from the theme
  useFontLoader(theme);

  useEffect(() => {
    // Apply theme to CSS variables
    const root = document.documentElement;

    // Set vendor attribute for potential CSS selectors
    root.setAttribute("data-vendor", currentVendor);

    // Apply colors
    if (theme.colors) {
      Object.entries(theme.colors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--color-${key}`, value);
        }
      });
    }

    // Apply typography
    if (theme.typography) {
      // Set font family
      root.style.setProperty("--font-family", theme.typography.fontFamily);

      // Set heading font family if available
      if (theme.typography.fontFamilyHeading) {
        root.style.setProperty(
          "--font-family-heading",
          theme.typography.fontFamilyHeading
        );
      }

      // Set font sizes
      if (theme.typography.fontSize) {
        Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
          if (value) {
            root.style.setProperty(`--font-size-${key}`, value);
          }
        });
      }

      // Set font weights
      if (theme.typography.fontWeight) {
        Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
          if (value) {
            root.style.setProperty(`--font-weight-${key}`, value);
          }
        });
      }

      // Set line heights
      if (theme.typography.lineHeight) {
        Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
          if (value) {
            root.style.setProperty(`--line-height-${key}`, value);
          }
        });
      }

      // Set letter spacing
      if (theme.typography.letterSpacing) {
        Object.entries(theme.typography.letterSpacing).forEach(
          ([key, value]) => {
            if (value) {
              root.style.setProperty(`--letter-spacing-${key}`, value);
            }
          }
        );
      }
    }

    // Apply spacing
    if (theme.spacing) {
      Object.entries(theme.spacing).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--spacing-${key}`, value);
        }
      });
    }

    // Apply border radius
    if (theme.borderRadius) {
      Object.entries(theme.borderRadius).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--border-radius-${key}`, value);
        }
      });
    }

    // Apply shadows
    if (theme.shadows) {
      Object.entries(theme.shadows).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--shadow-${key}`, value);
        }
      });
    }

    // Apply animations
    if (theme.animations) {
      Object.entries(theme.animations).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--animation-${key}`, value);
        }
      });
    }

    // Apply decorations
    if (theme.decorations) {
      Object.entries(theme.decorations).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--decoration-${key}`, value);
        }
      });
    }

    // Any other theme variables
    Object.entries(theme).forEach(([key, value]) => {
      // Skip the properties we've already processed
      if (
        [
          "name",
          "colors",
          "typography",
          "spacing",
          "borderRadius",
          "shadows",
          "animations",
          "decorations",
        ].includes(key)
      ) {
        return;
      }

      // Process any other top-level properties that might be objects
      if (value && typeof value === "object") {
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (typeof subValue === "string") {
            root.style.setProperty(`--${key}-${subKey}`, subValue);
          }
        });
      }
    });

    // Interface elements
    if (theme.interfaceElements) {
      dispatch(setThemeElements(theme.interfaceElements));
    }
  }, [theme, currentVendor, dispatch]);

  return (
    <>
      <Outlet />
    </>
  );
};
